import React from "react";
import { Typography } from "antd";

// ─── ErrorBoundary reutilizable para secciones de Finanzas Empresa ──────────
//
// Sin esto, cualquier error de render dentro de una sección (p.ej. una fila
// con un JSON malformado en `pagado_a`/`pagado_por`, un sorter que llama
// `.localeCompare` sobre `null`, un dayjs inválido, etc.) propaga al árbol
// de React y desmonta TODA la página → "pantalla en blanco" reportada en
// distintas pestañas.
//
// Originalmente este boundary vivía inline dentro de `finanzasSection.tsx`
// y solo cubría Sueldos/Gastos. Se extrajo a archivo propio para poder
// envolver también Arriendos / Aseos / Lavandería / Productos / Resumen
// desde `finanzasEmpresa.tsx` sin duplicar código.
//
// El boundary captura el error, lo loguea con stack visible para diagnóstico
// y muestra un fallback con `componentStack` para que el admin pueda
// reportarlo sin tener que abrir DevTools.

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  info: string;
}

interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  titulo: string;
}

export class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  state: SectionErrorBoundaryState = { hasError: false, error: null, info: "" };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, info: "" };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Loguear a consola con detalle — ayuda a diagnosticar reportes del
    // tipo "se va a blanco al entrar a Sueldos" sin tener que adivinar.
    // eslint-disable-next-line no-console
    console.error(
      `[FinanzasSection:${this.props.titulo}] crash en render:`,
      error,
      info?.componentStack,
    );
    this.setState({ info: info?.componentStack ?? "" });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ padding: 16 }}>
        <Typography.Title level={5} style={{ color: "#cf1322" }}>
          No se pudo cargar la pestaña {this.props.titulo}
        </Typography.Title>
        <Typography.Paragraph>
          {this.state.error?.message ?? "Error desconocido al renderizar."}
        </Typography.Paragraph>
        <details>
          <summary style={{ cursor: "pointer" }}>Detalle técnico</summary>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontSize: 12,
              background: "#fafafa",
              padding: 8,
              borderRadius: 4,
              maxHeight: 240,
              overflow: "auto",
            }}
          >
            {String(this.state.error?.stack ?? "")}
            {this.state.info ? `\n\n${this.state.info}` : ""}
          </pre>
        </details>
      </div>
    );
  }
}
